import {useEffect, useRef, useCallback} from '@wordpress/element';
import {useCreditCardContext} from "./use-credit-card-context";

export const useCreatePaymentMethod = (
    {
        billing,
        emitResponse,
        onPaymentSetup,
        needsSetupToken
    }) => {
    const currentData = useRef({});
    const {
        i18n,
        onFieldError,
        cardElement,
        cardFields,
        validatedFields,
        paymentMethodData,
        getPaymentMethodData
    } = useCreditCardContext();

    useEffect(() => {
        currentData.current = {
            billing,
            cardFields,
            cardElement,
            emitResponse,
            validatedFields,
            paymentMethodData
        }
    }, [
        billing,
        cardElement,
        cardFields,
        emitResponse,
        validatedFields,
        paymentMethodData
    ]);

    const validateCreditCardFields = useCallback(() => {
        const {
            cardFields,
            validatedFields
        } = currentData.current;

        if (!Object.keys(validatedFields).length) {
            Object.keys(cardFields).forEach(key => {
                if (validatedFields[key].isRequired) {
                    onFieldError(key);
                    cardFields[key].addClass('invalid');
                }
            });
            throw new Error(i18n.incomplete_form);
        }
        let isCardFormValid = true;
        Object.keys(validatedFields).forEach(key => {
            const {
                isValid,
                isEmpty,
                isRequired
            } = validatedFields[key];
            if (isRequired && (!isValid || isEmpty)) {
                onFieldError(key);
                cardFields[key].addClass('invalid');
                isCardFormValid = false;
            }
        });
        if (!isCardFormValid) {
            throw new Error(i18n.incomplete_form);
        }
    }, []);

    useEffect(() => {
        const unsubscribe = onPaymentSetup(async () => {
            const {
                billing,
                cardElement,
                emitResponse
            } = currentData.current;
            const {
                billingAddress
            } = billing;
            try {
                await validateCreditCardFields();

                await cardElement.submit({
                    ...(!needsSetupToken && {
                        billingAddress: {
                            addressLine1: billingAddress.address_1 || '',
                            addressLine2: billingAddress.address_2 || '',
                            adminArea1: billingAddress.state || '',
                            adminArea2: billingAddress.city || '',
                            postalCode: billingAddress.postcode || '',
                            countryCode: billingAddress.country || '',
                        }
                    })
                });

                const {
                    orderId = '',
                    paymentTokenId = ''
                } = getPaymentMethodData();

                return {
                    type: emitResponse.responseTypes.SUCCESS,
                    meta: {
                        paymentMethodData: {
                            ppcp_card_payment_token: paymentTokenId,
                            ppcp_card_paypal_order_id: orderId
                        }
                    }
                }
            } catch (error) {
                let message = error?.message || '';
                if (error?.message && ['INVALID_NUMBER', 'INVALID_EXPIRY', 'INVALID_CVV'].includes(error.message)) {
                    message = i18n.incomplete_form;
                }
                return {
                    type: emitResponse.responseTypes.ERROR,
                    message,
                    messageContext: emitResponse.noticeContexts.PAYMENTS
                }
            }
        });
        return unsubscribe;
    }, [
        i18n,
        onPaymentSetup,
        needsSetupToken,
        getPaymentMethodData,
        validateCreditCardFields
    ]);
}